const document = (type, label, mask, charset = 'digits', validator = null) => Object.freeze({
  type,
  label,
  mask,
  charset,
  validator,
});

const PERSON_DOCUMENTS_BY_COUNTRY = Object.freeze({
  AR: document('DNI', 'DNI', '00.000.000'),
  BR: document('CPF', 'CPF', '000.000.000-00', 'digits', 'cpf'),
  CA: document('SIN', 'SIN', '000-000-000'),
  CL: document('RUN', 'RUN / RUT', '00.000.000-A', 'alnum'),
  CO: document('NUIP', 'NUIP / Cédula', '0000000000'),
  CR: document('CEDULA', 'Cédula de Identidad', '0-0000-0000'),
  DO: document('CEDULA', 'Cédula de Identidad', '000-0000000-0'),
  EC: document('CEDULA', 'Cédula de Identidad', '0000000000'),
  GT: document('DPI', 'DPI', '0000 00000 0000'),
  MX: document('CURP', 'CURP', 'AAAA000000AAAAAAA0', 'alnum'),
  PE: document('DNI', 'DNI', '00000000'),
  US: document('SSN', 'SSN', '000-00-0000'),
  VE: document('CEDULA', 'Cédula de Identidad', 'A-00000000', 'alnum'),

  AL: document('NID', 'Número de Identidade', 'A00000000A', 'alnum'),
  AT: document('SVNR', 'SVNR', '0000 000000'),
  BA: document('JMBG', 'JMBG', '0000000000000'),
  BE: document('NRN', 'National Register Number', '00.00.00-000.00'),
  BG: document('EGN', 'EGN', '0000000000'),
  CH: document('AHV', 'AHV / AVS', '000.0000.0000.00'),
  CY: document('TIN', 'TIN', '00000000A', 'alnum'),
  CZ: document('BIRTH_NUMBER', 'Rodné číslo', '000000/0000'),
  DE: document('STEUER_ID', 'Steuer-ID', '00000000000'),
  DK: document('CPR', 'CPR', '000000-0000'),
  EE: document('ISIKUKOOD', 'Isikukood', '00000000000'),
  ES: document('DNI', 'DNI', '00000000A', 'alnum'),
  FI: document('HETU', 'HETU', '000000A000A', 'alnum'),
  FR: document('NIR', 'NIR', '0 00 00 00 000 000 00'),
  GB: document('NINO', 'National Insurance Number', 'AA 00 00 00 A', 'alnum'),
  GR: document('AFM', 'AFM', '000000000'),
  HR: document('OIB', 'OIB', '00000000000'),
  HU: document('PERSONAL_ID', 'Személyi azonosító', '00000000000'),
  IE: document('PPSN', 'PPS Number', '0000000AA', 'alnum'),
  IS: document('KENNITALA', 'Kennitala', '000000-0000'),
  IT: document('CODICE_FISCALE', 'Codice Fiscale', 'AAAAAAAAAAAAAAAA', 'alnum'),
  LT: document('PERSONAL_CODE', 'Asmens kodas', '00000000000'),
  LU: document('NATIONAL_ID', 'Matricule', '0000000000000'),
  LV: document('PERSONAL_CODE', 'Personas kods', '000000-00000'),
  ME: document('JMBG', 'JMBG', '0000000000000'),
  MK: document('EMBG', 'EMBG', '0000000000000'),
  NL: document('BSN', 'BSN', '000000000'),
  NO: document('FODSELSNUMMER', 'Fødselsnummer', '00000000000'),
  PL: document('PESEL', 'PESEL', '00000000000'),
  PT: document('NIF', 'NIF', '000000000'),
  RO: document('CNP', 'CNP', '0000000000000'),
  RS: document('JMBG', 'JMBG', '0000000000000'),
  SE: document('PERSONNUMMER', 'Personnummer', '000000-0000'),
  SI: document('EMSO', 'EMŠO', '0000000000000'),
  SK: document('BIRTH_NUMBER', 'Rodné číslo', '000000/0000'),
  TR: document('TCKN', 'T.C. Kimlik No.', '00000000000'),
  UA: document('RNOKPP', 'RNOKPP', '0000000000'),

  AE: document('EMIRATES_ID', 'Emirates ID', '000-0000-0000000-0'),
  BH: document('CPR', 'CPR', '000000000'),
  CN: document('RESIDENT_ID', 'Resident Identity Card', '00000000000000000A', 'alnum'),
  ID: document('NIK', 'NIK', '0000000000000000'),
  IL: document('TEUDAT_ZEHUT', 'Teudat Zehut', '000000000'),
  IN: document('AADHAAR', 'Aadhaar', '0000 0000 0000'),
  IR: document('NATIONAL_ID', 'National ID', '0000000000'),
  JP: document('MY_NUMBER', 'My Number', '000000000000'),
  KR: document('RRN', 'Resident Registration Number', '000000-0000000'),
  KW: document('CIVIL_ID', 'Civil ID', '000000000000'),
  MY: document('MYKAD', 'MyKad', '000000-00-0000'),
  PK: document('CNIC', 'CNIC', '00000-0000000-0'),
  PH: document('PHILSYS', 'PhilSys Number', '0000-0000-0000'),
  SA: document('NATIONAL_ID', 'National ID', '0000000000'),
  SG: document('NRIC', 'NRIC / FIN', 'A0000000A', 'alnum'),
  TH: document('NATIONAL_ID', 'Thai National ID', '0-0000-00000-00-0'),
  TW: document('NATIONAL_ID', 'National ID', 'A000000000', 'alnum'),
  VN: document('CITIZEN_ID', 'Citizen ID', '000000000000'),

  EG: document('NATIONAL_ID', 'National ID', '00000000000000'),
  NG: document('NIN', 'NIN', '00000000000'),
  ZA: document('ID_NUMBER', 'ID Number', '0000000000000'),
});

/*
 * Identificador principal de organização por país. A regra continua explícita:
 * não existe fallback genérico. País sem identificador empresarial configurado
 * retorna null, exatamente como ocorre no catálogo de pessoas.
 */
const ORGANIZATION_DOCUMENTS_BY_COUNTRY = Object.freeze({
  AR: document('CUIT', 'CUIT', '00-00000000-0'),
  AU: document('ABN', 'ABN', '00 000 000 000'),
  BE: document('ENTERPRISE_NUMBER', 'Enterprise Number', '0000.000.000'),
  BR: document('CNPJ', 'CNPJ', 'AA.AAA.AAA/AAAA-00', 'alnum', 'cnpj'),
  CA: document('BN', 'Business Number', '000000000'),
  CL: document('RUT', 'RUT', '00.000.000-A', 'alnum'),
  CN: document('USCC', 'Unified Social Credit Code', 'AAAAAAAAAAAAAAAAAA', 'alnum'),
  CO: document('NIT', 'NIT', '000000000-0'),
  ES: document('NIF', 'NIF', 'A00000000', 'alnum'),
  FR: document('SIREN', 'SIREN', '000 000 000'),
  IN: document('CIN', 'CIN', 'AAAAAAAAAAAAAAAAAAAAA', 'alnum'),
  IT: document('PARTITA_IVA', 'Partita IVA', '00000000000'),
  JP: document('CORPORATE_NUMBER', 'Corporate Number', '0000000000000'),
  MX: document('RFC', 'RFC', 'AAA000000AAA', 'alnum'),
  NL: document('KVK', 'KVK', '00000000'),
  NZ: document('NZBN', 'NZBN', '0000000000000'),
  PE: document('RUC', 'RUC', '00000000000'),
  PT: document('NIPC', 'NIPC / NIF', '000000000'),
  US: document('EIN', 'EIN', '00-0000000'),
  ZA: document('COMPANY_REGISTRATION', 'Company Registration', '0000/000000/00'),
});

const stripAccents = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const ALL_DOCUMENT_COUNTRY_CODES = Object.freeze([...new Set([
  ...Object.keys(PERSON_DOCUMENTS_BY_COUNTRY),
  ...Object.keys(ORGANIZATION_DOCUMENTS_BY_COUNTRY),
])]);

const localeNameIndexCache = new Map();

const localeCountryNameIndex = (locale) => {
  const key = String(locale || '').trim();
  if (!key) return null;
  if (localeNameIndexCache.has(key)) return localeNameIndexCache.get(key);

  let index = null;
  try {
    const displayNames = new Intl.DisplayNames([key], { type: 'region' });
    index = Object.fromEntries(
      ALL_DOCUMENT_COUNTRY_CODES
        .map((code) => [code, displayNames.of(code)])
        .filter(([code, name]) => typeof name === 'string' && name.trim() && name !== code)
        .map(([code, name]) => [stripAccents(name).trim().toUpperCase(), code])
    );
  } catch {
    index = null;
  }

  localeNameIndexCache.set(key, index);
  return index;
};

// Formulário/CSV livre não obriga quem preenche a conhecer o código ISO de
// 2 letras. O nome completo do país nunca é cravado aqui num idioma fixo —
// nem PT-BR nem inglês bateriam com alguém escrevendo em japonês, por
// exemplo. `Intl.DisplayNames` já resolve nome -> código no locale pedido
// usando os dados nativos do runtime (a mesma fonte que
// packages/ep/components/create/geography/data.js usa para o combo de país),
// sem exigir dicionário próprio nem lista mantida à mão. Aceita um ou mais
// locales: um CSV importado não necessariamente está escrito no idioma da
// sessão de quem importa, então o chamador pode combinar, por exemplo, o
// locale da sessão com os demais locales que o ecossistema suporta.
export const buildCountryNameIndex = (locales = 'pt-BR') => {
  const list = Array.isArray(locales) ? locales : [locales];
  const merged = {};
  for (const locale of list) {
    const index = localeCountryNameIndex(locale);
    if (index) Object.assign(merged, index);
  }
  return Object.freeze(merged);
};

export const normalizeCountryCode = (value, { nameIndex = null } = {}) => {
  const raw = String(value || '').trim();
  const code = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(code)) return code;
  if (!nameIndex) return '';
  return nameIndex[stripAccents(raw).trim().toUpperCase()] || '';
};

export const normalizeDocumentEntity = (value) => {
  const normalized = String(value || 'person').trim().toLowerCase();
  return normalized === 'organization' ? 'organization' : 'person';
};

export const normalizeDocumentNumber = (value, charset = 'alnum') => {
  const normalized = String(value || '').normalize('NFKC').trim().toUpperCase();
  return charset === 'digits'
    ? normalized.replace(/\D/g, '')
    : normalized.replace(/[^0-9A-Z]/g, '');
};

const validateCpf = (value) => {
  const cpf = normalizeDocumentNumber(value, 'digits');
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;

  const digit = (length) => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(cpf[index]) * (length + 1 - index);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return digit(9) === Number(cpf[9]) && digit(10) === Number(cpf[10]);
};

const validateCnpj = (value) => {
  const cnpj = normalizeDocumentNumber(value, 'alnum');
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj) || /^(.)\1{13}$/.test(cnpj)) return false;

  const digit = (base, weights) => {
    const sum = base.split('').reduce((acc, char, index) => acc + (char.charCodeAt(0) - 48) * weights[index], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const first = digit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = digit(cnpj.slice(0, 12) + first, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return first === Number(cnpj[12]) && second === Number(cnpj[13]);
};

const resolveEntityOption = (options = {}) => {
  if (typeof options === 'string') return normalizeDocumentEntity(options);
  return normalizeDocumentEntity(options?.entity);
};

const catalogFor = (entity) => entity === 'organization'
  ? ORGANIZATION_DOCUMENTS_BY_COUNTRY
  : PERSON_DOCUMENTS_BY_COUNTRY;

export const listCountryDocuments = (options = {}) => {
  const entity = resolveEntityOption(options);
  return Object.entries(catalogFor(entity))
    .map(([country, value]) => ({ country, ...value }));
};

export const getCountryDocument = (countryCode, options = {}) => {
  const country = normalizeCountryCode(countryCode, { nameIndex: options?.nameIndex });
  if (!country) return null;
  const entity = resolveEntityOption(options);
  const value = catalogFor(entity)[country];
  return value ? { country, ...value } : null;
};

export const validateDocumentNumber = (countryCode, value, options = {}) => {
  const current = getCountryDocument(countryCode, options);
  if (!current) return false;

  const number = normalizeDocumentNumber(value, current.charset);
  if (!number) return false;
  if (current.validator === 'cpf') return validateCpf(number);
  if (current.validator === 'cnpj') return validateCnpj(number);
  return true;
};

export const resolveDocumentIdentity = ({ country, number, entity = 'person', nameIndex = null } = {}) => {
  const current = getCountryDocument(country, { entity, nameIndex });
  if (!current) return null;

  const normalizedNumber = normalizeDocumentNumber(number, current.charset);
  if (!normalizedNumber) return null;

  return {
    country: current.country,
    entity: resolveEntityOption({ entity }),
    type: current.type,
    number: normalizedNumber,
    valid: validateDocumentNumber(current.country, normalizedNumber, { entity, nameIndex }),
    label: current.label,
    mask: current.mask,
    charset: current.charset,
    validator: current.validator,
  };
};

export default Object.freeze({
  getCountryDocument,
  listCountryDocuments,
  normalizeCountryCode,
  normalizeDocumentEntity,
  normalizeDocumentNumber,
  resolveDocumentIdentity,
  validateDocumentNumber,
});
